import { CheckForAvailableFundsModal } from 'components/order/escrow/escrowCreateEdit/checkForAvailableFundsModal/CheckForAvailableFundsModal';
import { useField } from 'formik';
import React, { useCallback, useState } from 'react';
import { ButtonTW } from 'tailwindComponents/buttonTW/ButtonTW';
import ReactDOM from 'react-dom';
import { ModalTarget } from '../../lineItem/ModalTarget.Constant';
import { useGetEscrowBalancesDataQuery } from 'API/order/escrow/EscrowBaseAPI';
import { useSelectCurrentOrderId } from 'selectors/order/selectCurrentOrder';
import { assertDefined } from 'utilities/Assertions';
import { useSendAchDisbursementMutation } from 'API/order/escrow/AchDisbursementAPI';
import { checkForMutationSuccess } from 'API/checkForMutationSuccess';
import { Tooltip } from 'ui-toolkit';
import { IACHDisbursementFormData } from 'types/order/escrow/achDisbursements/IACHDisbursementFormData';
import { ePermissions } from 'types/administration/userAdministration/ePermissions';
import { useHasPermissions } from '../../../../../accessControl/hooks/useHasPermissions';

export interface ISendACHButtonProps {
  baseName: string;
  achDisbursement: IACHDisbursementFormData;
  disabled?: boolean;
}

export const SendACHButton: React.FC<ISendACHButtonProps> = ({ baseName, disabled, achDisbursement }) => {
  const orderId = useSelectCurrentOrderId();
  assertDefined(orderId);
  const hasDisbursePermissions = useHasPermissions([ePermissions.EditEscrowInitiateDisbursement]);

  const { data } = useGetEscrowBalancesDataQuery(orderId);
  const [onSend] = useSendAchDisbursementMutation();
  const [{ value: amountCents }] = useField<number | undefined>(`${baseName}.amountCents`);
  const [{ value: holdback }] = useField<boolean | undefined>(`${baseName}.holdback`);

  const hasEnoughMoney = Math.round((data?.availableAmountDollars ?? 0) * 100) >= Math.round(amountCents ?? 0);
  const [showNotEnoughWarning, setShowNotEnoughWarning] = useState<boolean>(false);

  const onSendACHDisbursement = useCallback(async () => {
    const sendResult = await onSend({ achId: achDisbursement.id, orderId });
    checkForMutationSuccess(sendResult, 'Sending ACH');
  }, [orderId, achDisbursement.id, onSend]);

  const onStartSendProcess = useCallback(() => {
    if (hasEnoughMoney) {
      onSendACHDisbursement();
    } else {
      setShowNotEnoughWarning(true);
    }
  }, [hasEnoughMoney, setShowNotEnoughWarning, onSendACHDisbursement]);

  const onIgnoreWarning = useCallback(() => {
    setShowNotEnoughWarning(false);
    onSendACHDisbursement();
  }, [onSendACHDisbursement, setShowNotEnoughWarning]);

  const targetElement = document.getElementById(ModalTarget);

  return (
    <>
      {hasDisbursePermissions && (
        <Tooltip
          placement={'auto'}
          trigger={disabled || holdback ? 'hover' : 'none'}
          tooltipContent={holdback ? 'Cannot send due to holdback' : 'Please save pending changes'}
        >
          <ButtonTW
            type={'button'}
            onClick={onStartSendProcess}
            data-test-id={`${baseName}.send-ach-button`}
            disabled={disabled || holdback}
          >
            Send
          </ButtonTW>
        </Tooltip>
      )}
      {showNotEnoughWarning &&
        targetElement &&
        ReactDOM.createPortal(
          <CheckForAvailableFundsModal
            submitData={onIgnoreWarning}
            type={'ach'}
            handleClose={() => setShowNotEnoughWarning(false)}
          />,
          targetElement
        )}
      {showNotEnoughWarning && !targetElement && (
        <CheckForAvailableFundsModal
          submitData={onIgnoreWarning}
          type={'ach'}
          handleClose={() => setShowNotEnoughWarning(false)}
        />
      )}
    </>
  );
};
